import { Loading, Modal, TextInput } from 'carbon-components-react';
import '../grading/grading.styles.scss';
import { Edit, Plus, Trash } from 'react-feather';
import { ConfirmModal } from '../../../components/modal/confirm-modal/ConfirmModal.component';
import { useReducerSpread } from '../../../utils/common.util';
import { useCustomMutation } from '../../../utils/useCustomMutation';
import { deleteMethod, postMethod } from '../../../api/common';
import { useState } from 'react';
import CustomButton from '../../../components/custom-button/custom-btn.component';
import useSWR from 'swr';
import ComponentGuard from '../../../components/component-guard/ComponentGuard';

const initialData = {
  remark: '',
  minimum: '',
  maximum: '',
};

export function RemarkCategories({ isOpen, closeModal, setIsOpen }) {
  const [innerIsOpen, setInnerIsOpen] = useState('');
  const [formData, setFormData] = useReducerSpread(initialData);

  const { trigger, error, isMutating, reset } = useCustomMutation(
    'academic/grading/create-academic-report-remark-category',
    (url, { arg }) => postMethod(url, arg),
  );

  const {
    trigger: editCategory,
    error: editError,
    isMutating: isEditing,
    reset: resetEdit,
  } = useCustomMutation(
    'academic/grading/update-academic-report-remark-category',
    (url, { arg }) => postMethod(url, arg),
  );

  const { data, isLoading, mutate } = useSWR(
    isOpen ? 'academic/grading/list-academic-report-remark-category' : null,
  );

  const [selectedItem, setSelectedItem] = useState(null);
  const {
    trigger: deleteCategory,
    error: deleteError,
    isMutating: isDeleting,
  } = useCustomMutation(
    `academic/grading/delete-academic-report-remark-category/${selectedItem?.id}`,
    (url, { arg }) => deleteMethod(url, arg),
  );

  const isEdit = innerIsOpen === 'edit';
  const isAdd = innerIsOpen === 'add';

  function handleInnerClose() {
    setInnerIsOpen('');
    setIsOpen('categories');
    setFormData(initialData);
    resetEdit();
    reset();
  }

  return (
    <>
      <Modal
        id="remark-category-modal"
        className="remark-category-modal"
        hasScrollingContent
        passiveModal={true}
        open={isOpen}
        modalHeading={`Remark Category`}
        primaryButtonDisabled={false}
      >
        <ComponentGuard allowedRoles={['AcademicReportRemarksAccess']}>
          <button
            className="text-12 text-primary d-flex align-items-center font-bold pointer clean-btn"
            style={{ gap: '4px' }}
            onClick={(e) => {
              setInnerIsOpen('add');
              e.target.blur();
              closeModal();
            }}
          >
            <Plus size="14" strokeWidth="2px" />
            <span>Add New</span>
          </button>{' '}
        </ComponentGuard>

        <div className="mt-30">
          {isLoading ? <Loading small withOverlay={false} /> : null}
          {data?.length > 0 && (
            <table className="bx--data-table table-white">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Category</th>
                  <th>Raw Score Range</th>
                  <ComponentGuard
                    allowedRoles={['AcademicReportRemarksAccess']}
                  >
                    <th>Action</th>{' '}
                  </ComponentGuard>
                </tr>
              </thead>
              <tbody>
                {data?.map((item) => {
                  return (
                    <tr key={item.id}>
                      <td>{item?.id}</td>
                      <td>{item?.remark}</td>
                      <td>{item?.rawScoreRange}</td>
                      <ComponentGuard
                        allowedRoles={['AcademicReportRemarksAccess']}
                      >
                        <td style={{ display: 'flex', gap: '10px' }}>
                          <button
                            className="text-12 text-primary d-flex align-items-center font-bold pointer clean-btn "
                            style={{ gap: '4px' }}
                            onClick={() => {
                              setInnerIsOpen('edit');
                              setFormData({
                                id: item.id,
                                remark: item.remark,
                                minimum: item.rawScoreRange
                                  .split('-')[0]
                                  .trim(),
                                maximum: item.rawScoreRange
                                  .split('-')[1]
                                  .trim(),
                              });
                              closeModal();
                            }}
                          >
                            <Edit
                              size="14"
                              strokeWidth="2px"
                              className="text-primary"
                            />
                          </button>
                          <button
                            className="text-12 text-primary d-flex align-items-center font-bold pointer clean-btn "
                            style={{ gap: '4px' }}
                            onClick={() => {
                              setInnerIsOpen('delete');
                              setSelectedItem(item);
                            }}
                          >
                            <Trash
                              size="14"
                              strokeWidth="2px"
                              className="text-danger"
                            />
                          </button>
                        </td>
                      </ComponentGuard>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {data?.length === 0 && (
            <p className="text-14 text-grey">No data available yet.</p>
          )}
        </div>

        <div className="mt-40" style={{ marginBottom: '-35px' }}>
          <CustomButton
            style={{ display: 'flex', justifySelf: 'end' }}
            classes=" secondary font-bold"
            onClick={closeModal}
          >
            CLOSE
          </CustomButton>
        </div>
      </Modal>

      <ConfirmModal
        title="Delete Category"
        isOpen={selectedItem !== null && innerIsOpen === 'delete'}
        closeModal={() => setSelectedItem(null)}
        description={
          <>
            <div className="mt-20">
              Are you sure you want to delete this category?
            </div>
            <div className="mt-20">"{selectedItem?.remark}"</div>
          </>
        }
        actionText="DELETE"
        onClick={() => {
          deleteCategory(null, {
            onSuccess: () => {
              setSelectedItem(null);
              mutate();
            },
          });
        }}
        isLoading={isDeleting}
        errorMessage={deleteError ? deleteError.message : null}
        actionBtnClasses="danger"
      />

      <ConfirmModal
        title={isEdit ? 'Edit Category' : 'Add New Category'}
        isOpen={isEdit || isAdd}
        description={
          <>
            <div className="mt-20">
              <TextInput
                labelText="Category Name"
                placeholder="Enter Category"
                value={formData.remark}
                onChange={(e) =>
                  setFormData({
                    remark: e.target.value,
                  })
                }
              />
              <div className="mt-20" style={{ display: 'flex', gap: '10px' }}>
                <TextInput
                  labelText="Minimum Score"
                  placeholder="Enter Minimum Score"
                  type="number"
                  value={formData.minimum}
                  onChange={(e) =>
                    setFormData({
                      minimum: e.target.value,
                    })
                  }
                />
                <TextInput
                  labelText="Maximum Score"
                  placeholder="Enter Maximum Score"
                  type="number"
                  value={formData.maximum}
                  onChange={(e) =>
                    setFormData({
                      maximum: e.target.value,
                    })
                  }
                />
              </div>
            </div>
          </>
        }
        actionText={isEdit ? 'SAVE CHANGES' : 'ADD'}
        onClick={() => {
          const onSuccess = () => {
            mutate();
            handleInnerClose();
          };

          isEdit
            ? editCategory(formData, { onSuccess })
            : trigger(formData, { onSuccess });
        }}
        disabled={
          !formData.remark ||
          !formData.minimum ||
          !formData.maximum ||
          Number(formData.minimum) >= Number(formData.maximum)
        }
        isLoading={isMutating || isEditing}
        errorMessage={error?.message ?? editError?.message ?? null}
        closeModal={handleInnerClose}
        actionBtnClasses="primary"
      />
    </>
  );
}
